import classNames from "classnames";
import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * The right panel's trailing breathing room, only when there is something to scroll past.
 *
 * Both tabs (properties and styling) used to end in a fixed 68px spacer so the last row cleared the
 * panel edge once scrolled. It was there when nothing overflowed too, and on a screen whose rows
 * almost fit it was the spacer alone that made the box scroll. This one opens to that height only
 * while the content without it is taller than the scroll box. Its own height is taken out of the
 * measurement, so opening it can never be the reason it stays open.
 */
const findScrollParent = (element: HTMLElement): HTMLElement | null => {
  let node: HTMLElement | null = element.parentElement;
  while (node) {
    const overflowY: string = getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const RightPanelScrollEnd = (): JSX.Element => {
  const ref: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  useLayoutEffect((): (() => void) | undefined => {
    const spacer: HTMLDivElement | null = ref.current;
    const scroller: HTMLElement | null = spacer ? findScrollParent(spacer) : null;
    const host: HTMLElement | null = spacer?.parentElement ?? null;
    if (!spacer || !scroller || !host) {
      return undefined;
    }

    const measure = (): void => {
      const contentHeight: number = scroller.scrollHeight - spacer.offsetHeight;
      setIsOverflowing(contentHeight > scroller.clientHeight);
    };

    const resizeObserver: ResizeObserver = new ResizeObserver(measure);
    const observeContent = (): void => {
      resizeObserver.disconnect();
      resizeObserver.observe(scroller);
      Array.from(host.children).forEach((child: Element): void => {
        if (child !== spacer) {
          resizeObserver.observe(child);
        }
      });
    };
    // A new selection swaps the column's children; re-observe what is there now.
    const mutationObserver: MutationObserver = new MutationObserver((): void => {
      observeContent();
      measure();
    });

    observeContent();
    mutationObserver.observe(host, { childList: true });
    measure();

    return (): void => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      className={classNames("kuika-right-panel__scroll-end", {
        "kuika-right-panel__scroll-end--active": isOverflowing
      })}
      aria-hidden="true"
      data-testid="rightPanelScrollEnd"
    />
  );
};

export default RightPanelScrollEnd;
